/**
 * HATCH boundary tessellation via earcut.
 *
 * Converts a list of `HatchLoop` (one exterior + optional holes) into a
 * triangle index buffer suitable for `THREE.BufferGeometry.setIndex`.
 *
 * # Why earcut
 *
 * AutoCAD's HATCH fill is a single planar polygon possibly punctured by
 * one or more interior loops (donut). earcut is the fastest robust 2D
 * polygon-with-holes tessellator that ships in a small (~5 kB gz)
 * footprint and handles concavities, near-collinear vertices and
 * arbitrary winding without GLU-style numerical fallback. The same
 * tessellator is used by Mapbox GL, deck.gl and three.js's own
 * `ShapeUtils` fallback.
 *
 * # Loop semantics
 *
 * A HATCH may have MULTIPLE disjoint exterior loops (multi-island: e.g.
 * the user selects two separate closed shapes and applies one HATCH).
 * Each exterior loop (`isHole: false`) is the contour of one fill REGION;
 * every hole (`isHole: true`) is subtracted from the region whose
 * exterior CONTAINS it. We tessellate every region with a single
 * earcut-with-holes pass and concatenate the results, so each island is
 * filled — aligning the render with the pick (which selects inside ANY
 * exterior loop; see `raycast/distance.ts case "hatch"`). Loops with
 * `kind === "interior"` produced by the Hatch adapter map onto
 * `isHole: true`. Closed flag is honoured — the closing edge from the
 * last vertex back to the first is implicit in earcut's contract, so the
 * adapter must NOT duplicate the first vertex at the end.
 *
 * # Output shape
 *
 * The result carries:
 *   - `vertices`: the flat `[x0, y0, x1, y1, …]` array earcut uses
 *     internally. We return it so the caller can build a `THREE.Float32-
 *     BufferAttribute` directly.
 *   - `indices`: triangle index list, 3 indices per triangle.
 *   - `vertexZ`: per-vertex Z-coordinate (HATCH may carry an `elevation`
 *     scalar that places the polygon out of the XY-plane). One Z per
 *     vertex — adapter copies from `loop.vertices[i].z`.
 *
 * Empty input or zero usable loops returns an empty result; the renderer
 * then skips drawing for this hatch (CLAUDE.md §6 edge case 1: `Hatch with
 * 0 loops → skip`). When ≥1 loop is usable but NONE is tagged exterior
 * (e.g. an import whose `pathTypeFlag` lacks the exterior bit), the FIRST
 * usable loop is treated as the exterior and the rest as its holes — the
 * legacy single-region fallback, matching the pick (`isPointInFilledRegion`).
 */

import earcut from "earcut";
import { assignHolesToExteriors } from "@dxf-ai-editor/drawing-model";

import type { HatchLoop } from "../types";

/** Boundary loop input fed to `tessellateHatchLoops`. */
export interface TessellationLoopInput {
  vertices: { x: number; y: number; z: number }[];
  isHole: boolean;
}

/** Result of one earcut tessellation pass. */
export interface TessellationResult {
  /** Flat `[x0, y0, x1, y1, …]` packed for `BufferAttribute`. */
  positionsXY: Float32Array;
  /** Per-vertex Z (length = positionsXY.length / 2). */
  positionsZ: Float32Array;
  /** Triangle indices, 3 per triangle. */
  indices: Uint32Array;
}

/** Empty (zero-triangle) tessellation result. */
function emptyTessellationResult(): TessellationResult {
  return {
    positionsXY: new Float32Array(0),
    positionsZ: new Float32Array(0),
    indices: new Uint32Array(0),
  };
}

/**
 * Tessellate ONE fill region (a single exterior loop punctured by its
 * own holes) into a triangle list via a single earcut-with-holes pass.
 *
 * Loop vertices are concatenated into one flat `[x, y, x, y, …]` buffer
 * (earcut requires that layout). The exterior comes first; each hole is
 * appended and its start vertex index recorded in `holeIndices`. Holes
 * with fewer than 3 vertices are dropped (degenerate). The exterior is
 * assumed to have ≥3 vertices (the caller guarantees this).
 *
 * This is the original single-region body, extracted so that
 * `tessellateHatchLoops` can call it once per region (multi-island) and
 * concatenate the results — the single-region case stays byte-identical.
 */
function tessellateOneRegion(
  exterior: TessellationLoopInput,
  holes: TessellationLoopInput[],
): TessellationResult {
  const flatXY: number[] = [];
  const flatZ: number[] = [];
  const holeIndices: number[] = [];

  for (const v of exterior.vertices) {
    flatXY.push(v.x, v.y);
    flatZ.push(v.z);
  }
  for (const hole of holes) {
    if (hole.vertices.length < 3) continue;
    // Hole start = current vertex count (= half of flatXY length).
    holeIndices.push(flatXY.length / 2);
    for (const v of hole.vertices) {
      flatXY.push(v.x, v.y);
      flatZ.push(v.z);
    }
  }

  const indices = earcut(
    flatXY,
    holeIndices.length > 0 ? holeIndices : undefined,
    2,
  );

  return {
    positionsXY: new Float32Array(flatXY),
    positionsZ: new Float32Array(flatZ),
    indices: new Uint32Array(indices),
  };
}

/**
 * Tessellate a multi-island polygon-with-holes set into a triangle list.
 *
 * The input may carry MULTIPLE exterior loops (`isHole: false`). Each
 * exterior is the contour of one fill region; each hole (`isHole: true`)
 * is subtracted from the region whose exterior CONTAINS it (point-in-
 * polygon on a representative hole vertex). Every region is tessellated
 * independently via `tessellateOneRegion` and the results concatenated,
 * with each region's indices offset by the running vertex count.
 *
 * Single-region behaviour (exactly one exterior) is byte-identical to
 * the original single-pass implementation — exterior first, all holes
 * appended in input order — so existing donut output is unchanged.
 */
export function tessellateHatchLoops(
  loops: TessellationLoopInput[],
  hadTaggedExteriorOverride?: boolean,
): TessellationResult {
  // Was ANY EXTERIOR path TAGGED in the RAW source — BEFORE the walk dropped its
  // <3-vertex loops? This drives the no-exterior fallback decision: a hatch that
  // tagged an exterior but whose exterior DEGENERATED (<3 verts) must NOT promote
  // an interior loop to a filled exterior (PR #410 P3 — that would regress a
  // malformed hatch from invisible to visibly-filled-wrong). Only a hatch with NO
  // tagged exterior at all (genuine untagged import) earns the first-loop fallback.
  // Mirrors `isPointInFilledRegion`'s tagged-aware fallback so render and pick agree.
  //
  // The caller MUST pass `hadTaggedExteriorOverride` whenever the `loops` it hands
  // in have ALREADY had degenerate (<3-vertex) loops dropped (e.g. the scene render
  // path feeds the post-walk `geom.loops`, which no longer contains a degenerate
  // tagged exterior). Re-deriving the flag from such a lossy `loops` would read
  // FALSE and wrongly fire the fallback. The override carries the RAW-source bit
  // (`SceneGeometry.hadTaggedExterior`, computed pre-drop in
  // `buildHatchSceneGeometry`). When the caller's `loops` ARE the raw, untrimmed set
  // (e.g. the single explicit viewport-clip exterior, or unit tests passing whole
  // loops), the override is omitted and the local derive is exact.
  const hadTaggedExterior =
    hadTaggedExteriorOverride ?? loops.some((l) => l && !l.isHole);

  // Partition into exteriors / holes, skipping degenerate loops (<3 verts).
  const exteriors: TessellationLoopInput[] = [];
  const holes: TessellationLoopInput[] = [];
  for (const loop of loops) {
    if (!loop || loop.vertices.length < 3) continue;
    if (loop.isHole) holes.push(loop);
    else exteriors.push(loop);
  }

  // No USABLE exterior remains. Two cases:
  //   - The hatch TAGGED an exterior but it degenerated (<3 verts) → there is no
  //     valid fill contour; render EMPTY rather than promoting an interior loop
  //     to a spurious fill (PR #410 P3).
  //   - The hatch tagged NO exterior at all (untagged import — `pathTypeFlag`
  //     lacks the exterior bit so every loop is a hole) → legacy single-region
  //     fallback: the FIRST usable loop acts as the exterior, the rest as its
  //     holes, so the fill does not vanish. Mirrors the pick's no-exterior
  //     fallback (distance.ts / isPointInFilledRegion).
  if (exteriors.length === 0) {
    if (hadTaggedExterior || holes.length === 0) {
      return emptyTessellationResult();
    }
    const [firstUsable, ...rest] = holes;
    return tessellateOneRegion(firstUsable!, rest);
  }

  // Build one region per exterior, then assign each hole to its container.
  if (exteriors.length === 1) {
    // Fast path: a single region with ALL holes — byte-identical to the
    // original single-pass output (no point-in-polygon assignment needed).
    return tessellateOneRegion(exteriors[0]!, holes);
  }

  // Multi-island: assign each hole to its owning exterior via the SHARED
  // one-truth `assignHolesToExteriors` (the first exterior whose polygon
  // contains the hole's representative vertex; -1 = unowned → DROPPED). The pick
  // predicate `isPointInFilledRegion` consults the SAME function, so render and
  // pick cannot diverge on which region a hole carves — even when a hole lies
  // inside two overlapping/nested exteriors (PR #410 P2: the owner subtracts it,
  // the non-owning exterior keeps its fill there). A hole contained by no
  // exterior cannot be subtracted from any region, and attaching it to an
  // arbitrary exterior would carve a spurious notch into an island it does not
  // belong to (safer to drop than to corrupt a region).
  const owners = assignHolesToExteriors(exteriors, holes);
  const regionHoles: TessellationLoopInput[][] = exteriors.map(() => []);
  for (let h = 0; h < holes.length; h++) {
    const owner = owners[h]!;
    if (owner >= 0) regionHoles[owner]!.push(holes[h]!);
  }

  // Tessellate each region and merge: concat positions; offset indices by
  // the running vertex count (= positionsXY.length / 2 appended so far).
  const regions = exteriors.map((exterior, e) =>
    tessellateOneRegion(exterior, regionHoles[e]!),
  );

  let totalXY = 0;
  let totalZ = 0;
  let totalIdx = 0;
  for (const r of regions) {
    totalXY += r.positionsXY.length;
    totalZ += r.positionsZ.length;
    totalIdx += r.indices.length;
  }

  const positionsXY = new Float32Array(totalXY);
  const positionsZ = new Float32Array(totalZ);
  const indices = new Uint32Array(totalIdx);

  let xyOffset = 0;
  let zOffset = 0;
  let idxOffset = 0;
  let vertexOffset = 0;
  for (const r of regions) {
    positionsXY.set(r.positionsXY, xyOffset);
    positionsZ.set(r.positionsZ, zOffset);
    for (let i = 0; i < r.indices.length; i++) {
      indices[idxOffset + i] = r.indices[i]! + vertexOffset;
    }
    xyOffset += r.positionsXY.length;
    zOffset += r.positionsZ.length;
    idxOffset += r.indices.length;
    vertexOffset += r.positionsXY.length / 2;
  }

  return { positionsXY, positionsZ, indices };
}

/**
 * Adapter helper — convert the renderer's `HatchLoop[]` into the
 * `TessellationLoopInput[]` shape. Drops `bulges` (HATCH polygons fed
 * to earcut must be polygonal — bulge segments are pre-tessellated by
 * the adapter, see `hatchToRenderable`).
 */
export function hatchLoopsToTessellationInput(
  loops: HatchLoop[],
): TessellationLoopInput[] {
  return loops.map((loop) => ({
    vertices: loop.vertices.map((v) => ({ x: v.x, y: v.y, z: v.z })),
    isHole: loop.kind === "interior",
  }));
}
